import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaBan,
  FaCalendarAlt,
  FaDownload,
  FaFileInvoiceDollar,
  FaMoneyBillWave,
  FaReceipt,
  FaStickyNote,
} from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import PortalAlert from "./PortalAlert";
import PortalPayDialog from "./PortalPayDialog";
import { getPortalSession } from "./portalSession";
import { downloadPortalInvoicePdf, dueStatus, formatMoney, invoiceStatusTone } from "./TenantFinance";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function badgeClass(tone) {
  return tone === "neutral" ? "cportal-badge" : `cportal-badge cportal-badge--${tone}`;
}

function DetailSection({ icon: Icon, title, children, actions }) {
  return (
    <section className="cportal-card cportal-detail-section">
      <h2 className="cportal-detail-section-title">
        {Icon ? <Icon className="cportal-detail-section-icon" aria-hidden="true" /> : null}
        {title}
        {actions ? <span className="cportal-detail-section-actions">{actions}</span> : null}
      </h2>
      {children}
    </section>
  );
}

function TenantInvoiceView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const session = useMemo(() => getPortalSession(), []);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [payOpen, setPayOpen] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get(`/client/finance/invoices/${id}`);
        if (!active) return;
        setInvoice(res.data?.data || null);
      } catch (err) {
        if (active) setError(err.response?.data?.message || "Could not load this invoice.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  const currency = invoice?.Currency_Code || "";

  const handleDownload = async () => {
    if (!invoice) return;
    setDownloading(true);
    try {
      await downloadPortalInvoicePdf(invoice, session);
    } catch (_) {
      setError("Could not build the invoice PDF.");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="cportal-page">
        <p className="cportal-muted">Loading…</p>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="cportal-page">
        <button type="button" className="cportal-back-link" onClick={() => navigate("/portal/finance/payables")}>
          <FaArrowLeft /> Back to payables
        </button>
        <div className="cportal-alert cportal-alert-error">{error || "Invoice not found."}</div>
      </div>
    );
  }

  const canPay = (invoice.Is_Open ?? true) && Number(invoice.Balance_Amount || 0) > 0;
  const due = canPay ? dueStatus(invoice.Due_Date) : null;

  const totals = [
    ["Subtotal", invoice.Sub_Total],
    ["Tax", invoice.Tax_Amount],
    ["Discount", invoice.Discount_Amount],
    ["Total", invoice.Total_Amount],
    ["Paid", invoice.Paid_Amount],
    ["Balance", invoice.Balance_Amount],
  ];

  return (
    <div className="cportal-page cportal-finance">
      <button type="button" className="cportal-back-link" onClick={() => navigate("/portal/finance/payables")}>
        <FaArrowLeft /> Back to payables
      </button>

      <PortalAlert type="error" message={error} onClear={() => setError("")} />

      <div className="cportal-entity-hero">
        <div className="cportal-entity-hero-left">
          <div className="cportal-entity-avatar" aria-hidden="true">
            <FaFileInvoiceDollar size={24} />
          </div>
          <div className="cportal-entity-hero-meta">
            <h1 className="cportal-entity-hero-name">{invoice.Invoice_No || "Invoice"}</h1>
            <span className={badgeClass(invoiceStatusTone(invoice))}>
              {invoice.Is_Overdue ? "Overdue" : invoice.Status || "—"}
            </span>
          </div>
        </div>
        <div className="cportal-entity-hero-actions">
          {canPay ? (
            <button
              type="button"
              className="cportal-btn cportal-btn-primary cportal-finance-pay-now"
              onClick={() => setPayOpen(true)}
            >
              <FaMoneyBillWave aria-hidden="true" />
              <span className="cportal-btn-text">Pay {formatMoney(invoice.Balance_Amount, currency)}</span>
            </button>
          ) : null}
          <button
            type="button"
            className="cportal-btn cportal-btn-primary"
            onClick={handleDownload}
            disabled={downloading}
          >
            <FaDownload aria-hidden="true" />
            <span className="cportal-btn-text">{downloading ? "Preparing…" : "Download PDF"}</span>
          </button>
        </div>
      </div>

      <DetailSection icon={FaCalendarAlt} title="Invoice details">
        <div className="cportal-profile-view-grid">
          <div className="cportal-profile-view-item">
            <span className="cportal-profile-view-label">Invoice date</span>
            <span className="cportal-profile-view-value">{formatDate(invoice.Invoice_Date)}</span>
          </div>
          <div className="cportal-profile-view-item">
            <span className="cportal-profile-view-label">Due date</span>
            <span className="cportal-profile-view-value">
              {formatDate(invoice.Due_Date)}
              {due ? <span className={`cportal-due-pill cportal-due-pill--${due.tone}`}>{due.label}</span> : null}
            </span>
          </div>
          <div className="cportal-profile-view-item">
            <span className="cportal-profile-view-label">Billed by</span>
            <span className="cportal-profile-view-value">
              {invoice.Issuer_Company?.Display_Name || invoice.Issuer_Company?.Company_Name || "—"}
            </span>
          </div>
          {invoice.Bill_To?.Name ? (
            <div className="cportal-profile-view-item">
              <span className="cportal-profile-view-label">Billed to</span>
              <span className="cportal-profile-view-value">
                {invoice.Bill_To.Name}
                {invoice.Bill_To.GSTIN ? ` · GSTIN ${invoice.Bill_To.GSTIN}` : ""}
                {invoice.Bill_To.Tax_Id
                  ? ` · ${invoice.Bill_To.Tax_Id_Type || "Tax ID"} ${invoice.Bill_To.Tax_Id}`
                  : ""}
              </span>
            </div>
          ) : null}
          <div className="cportal-profile-view-item">
            <span className="cportal-profile-view-label">Plan</span>
            <span className="cportal-profile-view-value">
              {invoice.Plan_Name || invoice.Project_Name || "—"}
            </span>
          </div>
        </div>
      </DetailSection>

      <DetailSection icon={FaFileInvoiceDollar} title="Line items">
        <div className="cportal-table-wrap">
          <table className="cportal-table cportal-invoice-items">
            <thead>
              <tr>
                <th>Service</th>
                <th className="cportal-td-num">Qty</th>
                <th className="cportal-td-num">Rate</th>
                <th className="cportal-td-num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(invoice.items || []).length === 0 ? (
                <tr>
                  <td colSpan={4} className="cportal-muted">
                    No line items on this invoice.
                  </td>
                </tr>
              ) : (
                invoice.items.map((item) => (
                  <tr key={item.Item_Id}>
                    <td>
                      <strong>{item.Service_Name || "Service"}</strong>
                      {item.Description ? (
                        <div className="cportal-muted">{item.Description}</div>
                      ) : null}
                    </td>
                    <td className="cportal-td-num">{Number(item.Quantity || 0)}</td>
                    <td className="cportal-td-num">{formatMoney(item.Unit_Price, currency)}</td>
                    <td className="cportal-td-num">{formatMoney(item.Total_Amount, currency)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <dl className="cportal-invoice-totals">
          {totals.map(([label, value]) => (
            <div
              key={label}
              className={`cportal-invoice-total-row${
                label === "Total" || label === "Balance" ? " is-strong" : ""
              }`}
            >
              <dt>{label}</dt>
              <dd>{formatMoney(value, currency)}</dd>
            </div>
          ))}
        </dl>
      </DetailSection>

      <DetailSection icon={FaReceipt} title="Payment history">
        {(invoice.payments || []).length === 0 ? (
          <p className="cportal-muted">No payments recorded against this invoice yet.</p>
        ) : (
          <div className="cportal-table-wrap">
            <table className="cportal-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th className="cportal-td-num">Amount</th>
                  <th>Mode</th>
                  <th>Reference</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {invoice.payments.map((p) => (
                  <tr key={p.Payment_Id}>
                    <td>{formatDate(p.Payment_Date)}</td>
                    <td className="cportal-td-num">
                      {formatMoney(p.Amount, p.Currency_Code || currency)}
                    </td>
                    <td>{p.Payment_Mode_Name || "—"}</td>
                    <td>{p.Transaction_Ref || "—"}</td>
                    <td>{p.Payment_Status || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DetailSection>

      {invoice.Status === "Cancelled" ? (
        <DetailSection icon={FaBan} title="Cancelled">
          <p className="cportal-notes-text">
            {invoice.Cancelled_On ? `This invoice was cancelled on ${formatDate(invoice.Cancelled_On)}.` : "This invoice was cancelled."}
            {invoice.Cancel_Reason ? ` Reason: ${invoice.Cancel_Reason}` : ""}
          </p>
          {invoice.Replaced_By ? (
            <p className="cportal-notes-text">
              Replaced by{" "}
              <button
                type="button"
                className="cportal-back-link"
                onClick={() => navigate(`/portal/finance/payables/${invoice.Replaced_By.AR_Id}`)}
              >
                {invoice.Replaced_By.Invoice_No}
              </button>
            </p>
          ) : null}
        </DetailSection>
      ) : null}

      {invoice.Replaces_Invoice_No ? (
        <DetailSection icon={FaFileInvoiceDollar} title="Replacement invoice">
          <p className="cportal-notes-text">
            This invoice replaces cancelled invoice {invoice.Replaces_Invoice_No}.
          </p>
        </DetailSection>
      ) : null}

      {invoice.Notes ? (
        <DetailSection icon={FaStickyNote} title="Notes">
          <p className="cportal-notes-text">{invoice.Notes}</p>
        </DetailSection>
      ) : null}

      <PortalPayDialog open={payOpen} arId={invoice.AR_Id || id} onClose={() => setPayOpen(false)} />
    </div>
  );
}

export default TenantInvoiceView;
